import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomenclatureApi, type Nomenclature } from '@/api/catalogs';
import { refs, type AccountRule } from '@/api/refs';
import { accounts } from '@/api/documents';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import AccountSelect from '@/components/AccountSelect';
import Drawer from '@/components/Drawer';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Довідник «Рахунки обліку (правила)» — per-org матриця правил
 * (ref.account_rules, alembic 0263).
 *
 * Виміри (Номенклатура / Вид / Склад) порожні = «для всіх». Хоча б один
 * рахунок має бути заданий. Priority 1-10 — вища пріоритетність вибирається
 * першою при колізії правил.
 *
 * Патерн «view = wide, create/edit = narrow»: dropdown юросіб повний (read
 * дозволено усім accessible через RLS), але кнопки create/edit/delete
 * disabled там, де нема refs.account_rules.manage.
 */
const PERMISSION = 'refs.account_rules.manage';

interface FormState {
  id: string | null;
  nomenclature_id: string;
  nomenclature_kind_id: string;
  warehouse_id: string;
  account_id: string;
  transfer_account_id: string;
  off_balance_account_id: string;
  off_balance_transfer_account_id: string;
  priority: number;
  is_active: boolean;
  comment: string;
}

const EMPTY: FormState = {
  id: null,
  nomenclature_id: '',
  nomenclature_kind_id: '',
  warehouse_id: '',
  account_id: '',
  transfer_account_id: '',
  off_balance_account_id: '',
  off_balance_transfer_account_id: '',
  priority: 5,
  is_active: true,
  comment: '',
};

export default function AccountRulesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const [form, setForm] = useState<FormState | null>(null);

  // Фільтри списку (client-side: правил небагато, ~сотні на org).
  const [search, setSearch] = useState('');        // пошук по назві номенклатури
  const [kindFilter, setKindFilter] = useState(''); // '' = усі види
  const [whFilter, setWhFilter] = useState('');      // '' = усі склади
  const [accFilter, setAccFilter] = useState('');    // '' = усі рахунки (по account_id)

  const list = useQuery({
    queryKey: ['account-rules', orgId],
    queryFn: () => refs.accountRules.list(orgId),
    enabled: !!orgId,
  });

  // Резолв назв вимірів.
  const nomQ = useQuery({
    queryKey: ['nomenclature-all'],
    queryFn: () => nomenclatureApi.list({ limit: 1000 }),
  });
  const kindsQ = useQuery({ queryKey: ['nomenclature-kinds'], queryFn: refs.nomenclatureKinds.list });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', orgId],
    queryFn: () => refs.warehouses(orgId),
    enabled: !!orgId,
  });
  // Усі рахунки (балансові + забалансові) для резолву назв у гриді.
  const accountsQ = useQuery({
    queryKey: ['accounts', 'all'],
    queryFn: () => accounts.list({ only_postable: true, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });

  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);
  const kindName = useMemo(() => {
    const m = new Map<string, string>();
    kindsQ.data?.forEach((k) => m.set(k.id, k.name));
    return m;
  }, [kindsQ.data]);
  const warehouseName = useMemo(() => {
    const m = new Map<string, string>();
    warehousesQ.data?.forEach((w) => m.set(w.id, w.name));
    return m;
  }, [warehousesQ.data]);
  const accountLabel = useMemo(() => {
    const m = new Map<string, string>();
    accountsQ.data?.forEach((a) => m.set(a.id, `${a.code} — ${a.name}`));
    return m;
  }, [accountsQ.data]);
  // Чи придатний рахунок для проводки (дзеркало graceful резолвера: не група,
  // не забалансовий). Непридатний → у проводці спрацює fallback (хардкод).
  const accountUsable = useMemo(() => {
    const m = new Map<string, boolean>();
    accountsQ.data?.forEach((a) => m.set(a.id, !a.is_group_only && !a.is_off_balance));
    return m;
  }, [accountsQ.data]);

  const acc = (id: string | null) => (id ? accountLabel.get(id) ?? '…' : '—');

  // Персональні рахунки НОМЕНКЛАТУРИ (поля картки nomenclature.*_account_id).
  // Card-рахунок номенклатури — штатний механізм товарних проводок (живий, не legacy),
  // тому НЕ переносимо у правило; лише ПОКАЗУЄМО тут (як персональні рахунки оплат),
  // щоб бухгалтер бачив нестандартні рахунки конкретних товарів в одному місці.
  const NOM_ACCOUNT_COLS: { col: keyof Nomenclature; label: string }[] = [
    { col: 'account_id', label: 'Рахунок обліку' },
    { col: 'transfer_account_id', label: 'Рахунок передачі' },
    { col: 'off_balance_account_id', label: 'Забалансовий' },
    { col: 'off_balance_transfer_account_id', label: 'Забаланс. передачі' },
  ];
  const nomPersonalRows = useMemo(() => {
    const out: { rowId: string; nom: string; vidLabel: string; code: string;
                 usable: boolean; offBalCol: boolean }[] = [];
    for (const n of nomQ.data?.items ?? []) {
      for (const { col, label } of NOM_ACCOUNT_COLS) {
        const accId = n[col] as string | null;
        if (!accId) continue;
        const offBalCol = col === 'off_balance_account_id'
          || col === 'off_balance_transfer_account_id';
        out.push({
          rowId: `${n.id}:${String(col)}`,
          nom: n.name,
          vidLabel: label,
          code: (accountLabel.get(accId) ?? '…').split(' — ')[0] ?? '…',
          // Забалансові КОЛОНКИ легітимно тримають забалансовий рахунок → не лякаємо.
          usable: offBalCol ? true : (accountUsable.get(accId) ?? true),
          offBalCol,
        });
      }
    }
    return out.sort((a, b) => a.nom.localeCompare(b.nom) || a.vidLabel.localeCompare(b.vidLabel));
  }, [nomQ.data, accountLabel, accountUsable]);

  // Відфільтрований список (client-side). search — по назві номенклатури,
  // решта — точний збіг по виміру/рахунку.
  const filteredRules = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (list.data ?? []).filter((r) => {
      if (kindFilter && r.nomenclature_kind_id !== kindFilter) return false;
      if (whFilter && r.warehouse_id !== whFilter) return false;
      if (accFilter && r.account_id !== accFilter) return false;
      if (q) {
        const nm = r.nomenclature_id
          ? (r.nomenclature_name ?? nomName.get(r.nomenclature_id) ?? '')
          : '';
        if (!nm.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [list.data, search, kindFilter, whFilter, accFilter, nomName]);

  // Рахунки, що реально зустрічаються у правилах (для dropdown фільтра рахунку).
  const accountsInRules = useMemo(() => {
    const ids = new Set<string>();
    (list.data ?? []).forEach((r) => { if (r.account_id) ids.add(r.account_id); });
    return [...ids]
      .map((id) => ({ id, label: accountLabel.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [list.data, accountLabel]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['account-rules', orgId] });

  // ── Секція «Стандартні рахунки за видами» (модель «як у кадрах») ──
  // Стандарт рахунку обліку товарів = 281 (хардкод резолвера). Бухгалтер може
  // задати свій рахунок ВИДУ → правило account_rules з nomenclature_kind_id
  // (без товару/складу). Повернення до 281 → правило знімається.
  const STD_GOODS_ACCOUNT = '281';
  const accIdByCode = useMemo(() => {
    const m = new Map<string, string>();
    accountsQ.data?.forEach((a) => m.set(a.code, a.id));
    return m;
  }, [accountsQ.data]);

  // «Чисте» правило виду: kind заданий, номенклатура/склад порожні.
  const kindRuleOf = (kindId: string): AccountRule | null => {
    for (const r of list.data ?? []) {
      if (r.deletion_mark) continue;
      if (r.nomenclature_kind_id !== kindId) continue;
      if (r.nomenclature_id || r.warehouse_id) continue;
      if (!r.account_id) continue;
      return r;
    }
    return null;
  };

  interface KindStdRow {
    kindId: string; kindName: string; isService: boolean;
    rule: AccountRule | null; effectiveCode: string; isStandard: boolean; imported: boolean;
  }
  const kindRows = useMemo<KindStdRow[]>(() => {
    return (kindsQ.data ?? []).map((k) => {
      const rule = kindRuleOf(k.id);
      const code = rule?.account_id ? (accountLabel.get(rule.account_id) ?? '…').split(' — ')[0] : STD_GOODS_ACCOUNT;
      return {
        kindId: k.id, kindName: k.name, isService: !!k.is_service,
        rule, effectiveCode: code ?? STD_GOODS_ACCOUNT,
        isStandard: !rule, imported: !!rule?.external_source,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindsQ.data, list.data, accountLabel]);

  // Зміна рахунку виду: upsert правило виду АБО видалення (повернення до 281).
  const setKindAccount = useMutation({
    mutationFn: async ({ kindId, accountId }: { kindId: string; accountId: string }) => {
      const rule = kindRuleOf(kindId);
      const newCode = accountId ? (accountLabel.get(accountId) ?? '').split(' — ')[0] : '';
      const backToStd = !accountId || newCode === STD_GOODS_ACCOUNT;
      if (backToStd) {
        if (rule) await refs.accountRules.remove(rule.id);
        return;
      }
      if (rule) {
        await refs.accountRules.update(rule.id, { account_id: accountId });
      } else {
        await refs.accountRules.create({
          organization_id: orgId,
          nomenclature_kind_id: kindId,
          account_id: accountId,
          priority: 5, is_active: true,
          comment: 'Стандартний рахунок виду номенклатури',
        });
      }
    },
    onSuccess: invalidate,
  });

  const save = useMutation({
    mutationFn: () => {
      const f = form!;
      const payload = {
        organization_id: orgId,
        nomenclature_id: f.nomenclature_id || null,
        nomenclature_kind_id: f.nomenclature_kind_id || null,
        warehouse_id: f.warehouse_id || null,
        account_id: f.account_id || null,
        transfer_account_id: f.transfer_account_id || null,
        off_balance_account_id: f.off_balance_account_id || null,
        off_balance_transfer_account_id: f.off_balance_transfer_account_id || null,
        priority: f.priority,
        is_active: f.is_active,
        comment: f.comment.trim() || null,
      };
      if (f.id) {
        const { organization_id: _omit, ...rest } = payload;
        return refs.accountRules.update(f.id, rest);
      }
      return refs.accountRules.create(payload);
    },
    onSuccess: () => { invalidate(); setForm(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.accountRules.remove(id),
    onSuccess: () => { invalidate(); setForm(null); },
  });

  const startCreate = () => setForm({ ...EMPTY });
  const startEdit = (r: AccountRule) => setForm({
    id: r.id,
    nomenclature_id: r.nomenclature_id ?? '',
    nomenclature_kind_id: r.nomenclature_kind_id ?? '',
    warehouse_id: r.warehouse_id ?? '',
    account_id: r.account_id ?? '',
    transfer_account_id: r.transfer_account_id ?? '',
    off_balance_account_id: r.off_balance_account_id ?? '',
    off_balance_transfer_account_id: r.off_balance_transfer_account_id ?? '',
    priority: r.priority,
    is_active: r.is_active,
    comment: r.comment ?? '',
  });

  const cols = useMemo<ColumnDef<AccountRule>[]>(() => {
    // Назва з backend (не залежить від ліміту фронта); fallback на мапу.
    const nomLabel = (r: AccountRule) =>
      r.nomenclature_id
        ? (r.nomenclature_name ?? nomName.get(r.nomenclature_id) ?? '…')
        : '(усі)';
    const kindLabel = (r: AccountRule) =>
      r.nomenclature_kind_id
        ? (r.nomenclature_kind_name ?? kindName.get(r.nomenclature_kind_id) ?? '…')
        : '(усі)';
    const whLabel = (r: AccountRule) =>
      r.warehouse_id ? warehouseName.get(r.warehouse_id) ?? '…' : '(усі)';
    return [
      {
        // Без width — гнучка колонка: займає вільний простір.
        key: 'nomenclature', header: 'Номенклатура',
        sortValue: (r) => nomLabel(r).toLowerCase(),
        cell: (r) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {r.external_source && (
              <span title="Імпортовано з 1С" style={{
                fontSize: '0.7rem', fontWeight: 700, color: '#2563eb',
                border: '1px solid #bfdbfe', borderRadius: 4, padding: '0 4px',
              }}>I</span>
            )}
            {nomLabel(r)}
          </span>
        ),
      },
      {
        key: 'kind', header: 'Вид', width: 150,
        sortValue: (r) => kindLabel(r).toLowerCase(), cell: (r) => kindLabel(r),
      },
      {
        key: 'warehouse', header: 'Склад', width: 150,
        sortValue: (r) => whLabel(r).toLowerCase(), cell: (r) => whLabel(r),
      },
      {
        key: 'account', header: 'Рахунок обліку', width: 160,
        sortValue: (r) => acc(r.account_id), cell: (r) => acc(r.account_id),
      },
      {
        key: 'transfer', header: 'Передачі', width: 160,
        sortValue: (r) => acc(r.transfer_account_id), cell: (r) => acc(r.transfer_account_id),
      },
      {
        key: 'off_balance', header: 'Забалансовий', width: 160,
        sortValue: (r) => acc(r.off_balance_account_id), cell: (r) => acc(r.off_balance_account_id),
      },
      {
        key: 'off_balance_transfer', header: 'Передачі забал.', width: 160,
        sortValue: (r) => acc(r.off_balance_transfer_account_id),
        cell: (r) => acc(r.off_balance_transfer_account_id),
      },
      {
        key: 'priority', header: 'Priority', width: 110, align: 'center',
        sortValue: (r) => r.priority, cell: (r) => r.priority,
      },
      {
        key: 'is_active', header: 'Активно', width: 110, align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0), cell: (r) => (r.is_active ? '✓' : '—'),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomName, kindName, warehouseName, accountLabel]);

  const canManage = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canManage
      ? `Немає прав керувати правилами у цій юрособі (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  // Хоча б один рахунок має бути заданий (дзеркало backend-валідації).
  const hasAnyAccount = !!form && (
    !!form.account_id || !!form.transfer_account_id
    || !!form.off_balance_account_id || !!form.off_balance_transfer_account_id
  );
  const canSave = canManage && hasAnyAccount && !save.isPending;

  const selStyle = { width: 140, flex: '0 0 auto' } as const;

  return (
    <>
      <h2 style={{ margin: '0 0 0.5rem' }}>Рахунки обліку (правила)</h2>

      {/* Тулбар + фільтри — компактні, у стилі сторінки «Співробітники». */}
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={selStyle}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Пошук по назві…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 160, flex: '0 0 auto' }}
        />
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} style={selStyle}>
          <option value="">Вид: усі</option>
          {(kindsQ.data ?? []).map((k) => (
            <option key={k.id} value={k.id}>{k.name}</option>
          ))}
        </select>
        <select value={whFilter} onChange={(e) => setWhFilter(e.target.value)} style={selStyle}>
          <option value="">Склад: усі</option>
          {(warehousesQ.data ?? []).map((w) => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        <select value={accFilter} onChange={(e) => setAccFilter(e.target.value)} style={selStyle}>
          <option value="">Рахунок: усі</option>
          {accountsInRules.map((a) => (
            <option key={a.id} value={a.id}>{a.label}</option>
          ))}
        </select>
        {(search || kindFilter || whFilter || accFilter) && (
          <button onClick={() => { setSearch(''); setKindFilter(''); setWhFilter(''); setAccFilter(''); }}>
            Скинути
          </button>
        )}
        <span style={{ color: 'var(--muted)', fontSize: '0.85em' }}>
          {filteredRules.length} з {list.data?.length ?? 0}
        </span>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canManage}
          title={tooltipNew}
          onClick={startCreate}
        >
          + Новий
        </button>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 760, fontSize: '0.85em', margin: '0.25rem 0 0.5rem' }}>
        Правила визначають, який рахунок обліку застосувати у проводках. Виміри
        (Номенклатура / Вид / Склад) порожні = правило «для всіх»; при колізії —
        вища пріоритетність (Priority).
      </p>

      {/* СЕКЦIЯ 1 — стандартні рахунки за ВИДАМИ номенклатури (модель «як у кадрах») */}
      <h3 style={{ margin: '0.8rem 0 0.3rem', fontSize: '1rem' }}>
        Стандартні рахунки за видами номенклатури
      </h3>
      <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0 0 0.4rem', maxWidth: 760 }}>
        Кожен <b>вид</b> номенклатури (Запчастини, Матеріали, Послуга…) має
        <b> стандартний</b> рахунок обліку (зелений, за замовч. 281). Бухгалтер може
        задати свій — це діє на всі товари виду. Повернення до 281 прибирає
        перевизначення. Свої види з’являються тут автоматично. Бейдж <b>I</b> — з 1С.
      </p>
      <table className="data-table" style={{ width: '100%', fontSize: '0.88rem' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Вид номенклатури</th>
            <th style={{ textAlign: 'left', width: 280 }}>Рахунок обліку</th>
            <th style={{ textAlign: 'left', width: 150 }}>Тип</th>
          </tr>
        </thead>
        <tbody>
          {kindRows.map((k) => (
            <tr key={k.kindId}>
              <td>{k.kindName}</td>
              <td>
                {canManage ? (
                  <AccountSelect
                    value={k.rule?.account_id ?? (accIdByCode.get(STD_GOODS_ACCOUNT) ?? '')}
                    onChange={(id) => setKindAccount.mutate({ kindId: k.kindId, accountId: id })}
                    placeholder={`— стандарт ${STD_GOODS_ACCOUNT} —`}
                  />
                ) : <b>{k.effectiveCode}</b>}
              </td>
              <td>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {k.imported && (
                    <span title="Імпортовано з 1С" style={{
                      fontSize: '0.7rem', fontWeight: 700, color: '#2563eb',
                      border: '1px solid #bfdbfe', borderRadius: 4, padding: '0 4px',
                    }}>I</span>
                  )}
                  <span style={{ color: k.isStandard ? '#16a34a' : '#b45309', fontWeight: 600 }}>
                    {k.isStandard ? 'стандарт' : 'нестандарт'}
                  </span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* СЕКЦIЯ 2 — повна матриця правил (товари / склад / пріоритет) */}
      <h3 style={{ margin: '1.2rem 0 0.3rem', fontSize: '1rem' }}>
        Усі правила (товари, склади, пріоритети)
      </h3>
      <DataTable<AccountRule>
        rows={filteredRules}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає правил."
        onRowDoubleClick={(r) => { if (canManage) startEdit(r); }}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="nomenclature"
      />

      {nomPersonalRows.length > 0 && (
        <div style={{ marginTop: '1.2rem' }}>
          <h3 style={{ margin: '0 0 0.3rem', fontSize: '1rem' }}>
            Персональні рахунки номенклатури
          </h3>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '0 0 0.4rem' }}>
            Нестандартні рахунки конкретних товарів/послуг (задані на картці
            номенклатури). Вони перебивають загальні правила вище. Редагуються на
            картці номенклатури.
          </p>
          <table className="data-table" style={{ width: '100%', fontSize: '0.88rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Номенклатура</th>
                <th style={{ textAlign: 'left' }}>Призначення</th>
                <th style={{ textAlign: 'left' }}>Рахунок</th>
              </tr>
            </thead>
            <tbody>
              {nomPersonalRows.map((p) => (
                <tr key={p.rowId}>
                  <td>{p.nom}</td>
                  <td>{p.vidLabel}</td>
                  <td>
                    <b style={p.usable ? undefined : { color: '#dc2626', textDecoration: 'line-through' }}>
                      {p.code}
                    </b>
                    {!p.usable && (
                      <span
                        title="Рахунок непридатний для проводки (забалансовий / група / видалений). У реальній проводці спрацює стандартний рахунок. Виправте на картці номенклатури."
                        style={{ color: '#dc2626', fontSize: '0.78rem', marginLeft: 6 }}
                      >
                        ⚠ не застосовується
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <Drawer
          open={!!form}
          title={form.id ? 'Редагувати правило' : 'Нове правило'}
          onClose={() => setForm(null)}
          footer={
            <>
              {form.id && (
                <button
                  className="danger"
                  onClick={() => confirm('Видалити правило?').then((ok) => { if (ok && form.id) remove.mutate(form.id); })}
                >
                  Видалити
                </button>
              )}
              <button onClick={() => setForm(null)}>Скасувати</button>
              <button
                className="primary"
                disabled={!canSave}
                title={!hasAnyAccount ? 'Задайте хоча б один рахунок' : undefined}
                onClick={() => save.mutate()}
              >
                Зберегти
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ color: 'var(--muted)', margin: 0, fontSize: '0.9em' }}>
              Порожні виміри = правило «для всіх». Хоча б один рахунок має бути
              заданий.
            </p>
            <label>Номенклатура
              <select value={form.nomenclature_id}
                onChange={(e) => setForm({ ...form, nomenclature_id: e.target.value })}>
                <option value="">— усі —</option>
                {(nomQ.data?.items ?? []).map((n) => (
                  <option key={n.id} value={n.id}>{n.name}</option>
                ))}
              </select>
            </label>
            <label>Вид номенклатури
              <select value={form.nomenclature_kind_id}
                onChange={(e) => setForm({ ...form, nomenclature_kind_id: e.target.value })}>
                <option value="">— усі —</option>
                {(kindsQ.data ?? []).map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </select>
            </label>
            <label>Склад
              <select value={form.warehouse_id}
                onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}>
                <option value="">— усі —</option>
                {(warehousesQ.data ?? []).map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </label>

            <label>Рахунок обліку
              <AccountSelect value={form.account_id}
                onChange={(id) => setForm({ ...form, account_id: id })}
                placeholder="— не задано —" />
            </label>
            <label>Рахунок передачі
              <AccountSelect value={form.transfer_account_id}
                onChange={(id) => setForm({ ...form, transfer_account_id: id })}
                placeholder="— не задано —" />
            </label>
            <label>Забалансовий рахунок обліку
              <AccountSelect value={form.off_balance_account_id} offBalance
                onChange={(id) => setForm({ ...form, off_balance_account_id: id })}
                placeholder="— не задано —" />
            </label>
            <label>Забалансовий рахунок передачі
              <AccountSelect value={form.off_balance_transfer_account_id} offBalance
                onChange={(id) => setForm({ ...form, off_balance_transfer_account_id: id })}
                placeholder="— не задано —" />
            </label>

            <label>Пріоритет (1-10)
              <input type="number" min={1} max={10} value={form.priority}
                onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} />
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Активне
            </label>
            <label>Коментар
              <input value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            </label>
          </div>
        </Drawer>
      )}
    </>
  );
}
